import { memo, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { format, parseISO, isValid } from 'date-fns';
import { cellBaseStyle, headerCellStyle, formatTime } from './reportTableStyles';

/**
 * Multi-Corporation Daily Attendance Table
 * Displays daily rolled up punch times, employee counts, scheduled/actual hours, overtime, and risk by Corp/PC/Dept/Job.
 */
const MultiCorpDailyTable = ({
  rawGridDetails = [],
  hasProfitCenter = false,
  activeFields = [],
}) => {
  const isVarianceOtActive = useMemo(() => {
    return (
      activeFields.includes('varianceOvertimeHrs') ||
      activeFields.includes('varianceHrs') ||
      activeFields.includes('overtimeHrs')
    );
  }, [activeFields]);

  const dailyHeaders = useMemo(() => [
    { id: 'corp', label: 'Corporation', align: 'center', minWidth: 200 },
    ...(hasProfitCenter ? [{ id: 'profitCenter', label: 'Profit Center', align: 'center', minWidth: 120 }] : []),
    { id: 'dept', label: 'Department', align: 'center', minWidth: 140 },
    { id: 'job', label: 'Job Title', align: 'center', minWidth: 140 },
    { id: 'date', label: 'Date', align: 'center', minWidth: 110 },
    { id: 'empCount', label: 'Employee count', align: 'center', minWidth: 120 },
    ...(activeFields.includes('punchIn') ? [{ id: 'punchIn', label: 'Punch In', align: 'center', minWidth: 100 }] : []),
    ...(activeFields.includes('punchOut') ? [{ id: 'punchOut', label: 'Punch Out', align: 'center', minWidth: 100 }] : []),
    ...(activeFields.includes('scheduledHrs') ? [{ id: 'scheduledHrs', label: 'Scheduled Hrs', align: 'center', minWidth: 110 }] : []),
    ...(activeFields.includes('actualHrs') ? [{ id: 'actualHrs', label: 'Actual Hrs', align: 'center', minWidth: 110 }] : []),
    ...(isVarianceOtActive ? [{ id: 'varianceOvertimeHrs', label: 'Variance Hours /OT Hrs', align: 'center', minWidth: 160 }] : []),
    ...(activeFields.includes('otPercent') ? [{ id: 'otPercent', label: 'OT%', align: 'center', minWidth: 90 }] : []),
    ...(activeFields.includes('otRisk') ? [{ id: 'otRisk', label: 'OT Risk', align: 'center', minWidth: 90 }] : []),
  ], [hasProfitCenter, activeFields, isVarianceOtActive]);

  const dailyRows = useMemo(() => {
    const rows = [];
    if (!rawGridDetails || rawGridDetails.length === 0) return rows;

    // Group records by Corporation -> then by Profit Center
    const corpMap = new Map();

    rawGridDetails.forEach((item) => {
      const corpKey = item.CorporationID || item.CorporationName || 'Corp';
      const corpName = item.CorporationName || 'Corporation';
      const pcKey = item.PCID || item.PCName || 'All';
      const pcName = item.PCName || (item.PCID ? `PC ${item.PCID}` : '-');

      if (!corpMap.has(corpKey)) {
        corpMap.set(corpKey, {
          corpName,
          pcMap: new Map(),
        });
      }

      const corpObj = corpMap.get(corpKey);
      if (!corpObj.pcMap.has(pcKey)) {
        corpObj.pcMap.set(pcKey, {
          pcName,
          records: [],
        });
      }

      corpObj.pcMap.get(pcKey).records.push(item);
    });

    let allCorpsSched = 0;
    let allCorpsActual = 0;
    let allCorpsOT = 0;
    let allCorpsVar = 0;
    let allCorpsMaxEmp = 0;

    corpMap.forEach((corpObj) => {
      let corpSched = 0;
      let corpActual = 0;
      let corpOT = 0;
      let corpVar = 0;
      let corpMaxEmp = 0;

      corpObj.pcMap.forEach((pcObj) => {
        let pcSched = 0;
        let pcActual = 0;
        let pcOT = 0;
        let pcVar = 0;
        let pcMaxEmp = 0;

        // Group by Department & Job
        const deptJobMap = new Map();
        pcObj.records.forEach((rec) => {
          const deptName = rec.NimbleDeptName || rec.DepartmentName || rec.deptName || '';
          const jobName = rec.NimbleJobName || rec.JobName || rec.jobTitle || '';
          const djKey = `${deptName}___${jobName}`;
          if (!deptJobMap.has(djKey)) {
            deptJobMap.set(djKey, { deptName, jobName, records: [] });
          }
          deptJobMap.get(djKey).records.push(rec);
        });

        deptJobMap.forEach((djGroup) => {
          let djSched = 0;
          let djActual = 0;
          let djOT = 0;
          let djVar = 0;
          let djMaxEmp = 0;

          djGroup.records.forEach((rec) => {
            const sched = Number(rec.ScheduledHours || rec.ScheduledHrs || rec.scheduledHrs || 0);
            const actual = Number(rec.ActualHours || 0);
            const ot = Number(rec.OTHours || 0);
            const varH = Number(rec.VarianceHours || 0);
            const empCount = Number(rec.EmployeeCount || 0);

            djSched += sched;
            djActual += actual;
            djOT += ot;
            djVar += varH;
            if (empCount > djMaxEmp) djMaxEmp = empCount;

            let otPStr = '0.00%';
            if (rec.OTPercent !== undefined && rec.OTPercent !== null) {
              otPStr = `${Number(rec.OTPercent).toFixed(2)}%`;
            } else if (actual > 0) {
              otPStr = `${((ot / actual) * 100).toFixed(2)}%`;
            }

            let dateStr = '';
            const dateVal = rec.Date || rec.WeekStartDate || rec.WeekEndDate;
            if (dateVal) {
              try {
                dateStr = isValid(parseISO(dateVal))
                  ? format(parseISO(dateVal), 'MM-dd-yyyy')
                  : dateVal;
              } catch {
                dateStr = dateVal;
              }
            }

            rows.push({
              isSummary: false,
              isGrand: false,
              isDeptJobHeader: false,
              corp: corpObj.corpName,
              pc: pcObj.pcName,
              dept: rec.NimbleDeptName || rec.DepartmentName || rec.deptName || '-',
              job: rec.NimbleJobName || rec.JobName || rec.jobTitle || '-',
              date: dateStr,
              emp: String(empCount || 0),
              punchIn: formatTime(rec.PunchIn || rec.punchIn),
              punchOut: formatTime(rec.PunchOut || rec.punchOut),
              sched: sched.toFixed(2),
              actual: actual.toFixed(2),
              ot: ot.toFixed(2),
              varH: varH.toFixed(2),
              otP: otPStr,
              otR: rec.OTRisk || (ot > 2 ? 'High' : ot > 0 ? 'Medium' : 'Low'),
            });
          });

          pcSched += djSched;
          pcActual += djActual;
          pcOT += djOT;
          pcVar += djVar;
          if (djMaxEmp > pcMaxEmp) pcMaxEmp = djMaxEmp;
        });

        if (hasProfitCenter) {
          // Profit Center Total Row
          const pcOtPercent = pcActual > 0 ? `${((pcOT / pcActual) * 100).toFixed(2)}%` : '0.00%';
          const pcOtRisk = pcOT > pcActual * 0.1 ? 'High' : pcOT > 0 ? 'Medium' : 'Low';

          rows.push({
            isSummary: true,
            isGrand: false,
            isDeptJobHeader: false,
            corp: corpObj.corpName,
            pc: `${pcObj.pcName === '-' ? corpObj.corpName : pcObj.pcName} Total`,
            date: '',
            emp: String(pcMaxEmp),
            punchIn: '-',
            punchOut: '-',
            sched: pcSched.toFixed(2),
            actual: pcActual.toFixed(2),
            ot: pcOT.toFixed(2),
            varH: pcVar.toFixed(2),
            otP: pcOtPercent,
            otR: pcOtRisk,
          });
        }

        corpSched += pcSched;
        corpActual += pcActual;
        corpOT += pcOT;
        corpVar += pcVar;
        if (pcMaxEmp > corpMaxEmp) corpMaxEmp = pcMaxEmp;
      });

      // Corporation Grand Total Row
      const corpOtPercent = corpActual > 0 ? `${((corpOT / corpActual) * 100).toFixed(2)}%` : '0.00%';
      const corpOtRisk = corpOT > corpActual * 0.1 ? 'High' : corpOT > 0 ? 'Medium' : 'Low';

      rows.push({
        isSummary: true,
        isGrand: true,
        isDeptJobHeader: false,
        corp: hasProfitCenter ? corpObj.corpName : `${corpObj.corpName} Total`,
        pc: 'Grand Total',
        date: '',
        emp: String(corpMaxEmp),
        punchIn: '-',
        punchOut: '-',
        sched: corpSched.toFixed(2),
        actual: corpActual.toFixed(2),
        ot: corpOT.toFixed(2),
        varH: corpVar.toFixed(2),
        otP: corpOtPercent,
        otR: corpOtRisk,
      });

      allCorpsSched += corpSched;
      allCorpsActual += corpActual;
      allCorpsOT += corpOT;
      allCorpsVar += corpVar;
      allCorpsMaxEmp += corpMaxEmp;
    });

    // Overall Combined Total Row if multiple corporations
    if (corpMap.size > 1) {
      const allOtPercent = allCorpsActual > 0 ? `${((allCorpsOT / allCorpsActual) * 100).toFixed(2)}%` : '0.00%';
      const allOtRisk = allCorpsOT > allCorpsActual * 0.1 ? 'High' : allCorpsOT > 0 ? 'Medium' : 'Low';

      rows.push({
        isSummary: true,
        isGrand: true,
        isDeptJobHeader: false,
        corp: 'All Corporations',
        pc: 'Grand Total',
        date: '',
        emp: String(allCorpsMaxEmp),
        punchIn: '-',
        punchOut: '-',
        sched: allCorpsSched.toFixed(2),
        actual: allCorpsActual.toFixed(2),
        ot: allCorpsOT.toFixed(2),
        varH: allCorpsVar.toFixed(2),
        otP: allOtPercent,
        otR: allOtRisk,
      });
    }

    return rows;
  }, [rawGridDetails, hasProfitCenter]);

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: '1px solid #d9d9d9',
          borderRadius: 0,
          flex: 1,
          height: '100%',
          maxHeight: '100%',
          overflow: 'auto',
          backgroundColor: '#ffffff',
        }}
      >
        <Table stickyHeader sx={{ width: 'max-content', minWidth: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto' }}>
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 100, backgroundColor: '#ffffff' }}>
            <TableRow>
              {dailyHeaders.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align}
                  sx={{
                    ...headerCellStyle,
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    color: '#0f172a',
                    backgroundColor: '#ffffff',
                    py: 1.25,
                    minWidth: col.minWidth,
                    textAlign: col.align || 'center',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {dailyRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={dailyHeaders.length}
                  align="center"
                  sx={{ ...cellBaseStyle, py: 4, textAlign: 'center' }}
                >
                  No attendance records found for this period.
                </TableCell>
              </TableRow>
            ) : (
              dailyRows.map((r, idx) => {
                if (r.isDeptJobHeader) {
                  return (
                    <TableRow
                      key={`daily-deptjob-header-${idx}`}
                      sx={{
                        backgroundColor: 'transparent',
                      }}
                    >
                      <TableCell
                        colSpan={dailyHeaders.length}
                        align="center"
                        sx={{
                          ...cellBaseStyle,
                          fontWeight: 700,
                          fontSize: '0.78rem',
                          textAlign: 'center',
                          py: 1,
                          backgroundColor: 'transparent',
                        }}
                      >
                        {r.title}
                      </TableCell>
                    </TableRow>
                  );
                }

                const bg = r.isGrand ? '#e2e8f0' : r.isSummary ? '#f1f5f9' : '#ffffff';
                const fw = r.isSummary ? 700 : 400;

                return (
                  <TableRow
                    key={`daily-row-${r.corp || 'corp'}-${r.pc || 'pc'}-${r.date || 'd'}-${idx}`}
                    sx={{
                      backgroundColor: bg,
                      '&:hover': {
                        backgroundColor: r.isSummary ? bg : '#fafafa',
                      },
                    }}
                  >
                    <TableCell align="center" sx={{ ...cellBaseStyle, fontWeight: fw, textAlign: 'center' }}>
                      {r.corp}
                    </TableCell>
                    {hasProfitCenter && (
                      <TableCell align="center" sx={{ ...cellBaseStyle, fontWeight: fw, textAlign: 'center' }}>
                        {r.pc}
                      </TableCell>
                    )}
                    <TableCell align="center" sx={{ ...cellBaseStyle, fontWeight: fw, textAlign: 'center' }}>
                      {r.dept || '-'}
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellBaseStyle, fontWeight: fw, textAlign: 'center' }}>
                      {r.job || '-'}
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellBaseStyle, textAlign: 'center' }}>
                      {r.date}
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellBaseStyle, fontWeight: fw, textAlign: 'center' }}>
                      {r.emp}
                    </TableCell>
                    {activeFields.includes('punchIn') && (
                      <TableCell align="center" sx={{ ...cellBaseStyle, textAlign: 'center' }}>
                        {r.punchIn || '-'}
                      </TableCell>
                    )}
                    {activeFields.includes('punchOut') && (
                      <TableCell align="center" sx={{ ...cellBaseStyle, textAlign: 'center' }}>
                        {r.punchOut || '-'}
                      </TableCell>
                    )}
                    {activeFields.includes('scheduledHrs') && (
                      <TableCell align="center" sx={{ ...cellBaseStyle, fontWeight: fw, textAlign: 'center' }}>
                        {r.sched}
                      </TableCell>
                    )}
                    {activeFields.includes('actualHrs') && (
                      <TableCell align="center" sx={{ ...cellBaseStyle, fontWeight: fw, textAlign: 'center' }}>
                        {r.actual}
                      </TableCell>
                    )}
                    {isVarianceOtActive && (
                      <TableCell align="center" sx={{ ...cellBaseStyle, fontWeight: fw, textAlign: 'center' }}>
                        {r.varH}
                      </TableCell>
                    )}
                    {activeFields.includes('otPercent') && (
                      <TableCell align="center" sx={{ ...cellBaseStyle, fontWeight: fw, textAlign: 'center' }}>
                        {r.otP}
                      </TableCell>
                    )}
                    {activeFields.includes('otRisk') && (
                      <TableCell align="center" sx={{ ...cellBaseStyle, fontWeight: fw, textAlign: 'center' }}>
                        {r.otR}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default memo(MultiCorpDailyTable);
